import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { SolutionMapEntry } from '../../../client/IvtHubClient';
import {
  PRIORITY_BG,
  PRIORITY_BG_EDIT,
  PRIORITY_BORDER,
  priorityTier,
} from '../../../utils/priority';
import {
  computeSpanRange,
  readEntryNotes,
  spanRowStyle,
} from '../utils/spanCoverage';

interface SpanRowProps {
  entry: SolutionMapEntry;
  areaNames: string[];
  editMode?: boolean;
  locked?: boolean;
  highlighted?: boolean;
  dimmed?: boolean;
  onCyclePriority?: (entry: SolutionMapEntry) => void;
  onRename?: (entry: SolutionMapEntry, newName: string) => void;
  onSoftDelete?: (entry: SolutionMapEntry) => void;
  onUpdateCoverage?: (entry: SolutionMapEntry, coveredAreas: string[]) => void;
}

export default function SpanRow({
  entry,
  areaNames,
  editMode = false,
  locked = false,
  highlighted = false,
  dimmed = false,
  onCyclePriority,
  onRename,
  onSoftDelete,
  onUpdateCoverage,
}: SpanRowProps) {
  const notes = readEntryNotes(entry);
  const range = computeSpanRange(notes, areaNames);
  const [localRange, setLocalRange] = useState(range);
  const style = spanRowStyle(editMode ? localRange : range, areaNames.length);
  const tier = priorityTier(entry.priority);
  const useCase = entry.useCase ?? '';

  useEffect(() => {
    setLocalRange(range);
  }, [range.startCol, range.endCol]);

  const [renaming, setRenaming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (renaming && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [renaming]);

  const commitRename = () => {
    if (!textareaRef.current) return;
    const raw = textareaRef.current.value.trim().replace(/\n/g, ' ');
    if (raw && raw !== useCase) onRename?.(entry, raw);
    setRenaming(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setRenaming(false);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitRename();
    }
  };

  const commitCoverage = useCallback((newRange: { startCol: number; endCol: number }) => {
    const covered = areaNames.slice(newRange.startCol, newRange.endCol + 1);
    if (onUpdateCoverage) {
      onUpdateCoverage(entry, covered);
    }
  }, [areaNames, entry, onUpdateCoverage]);

  const resizingRef = useRef(false);

  const handleEdgeMouseDown = useCallback((edge: 'left' | 'right', e: React.MouseEvent) => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;

    const parent = containerRef.current?.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const colWidth = parentRect.width / areaNames.length;

    const onMouseMove = (ev: MouseEvent) => {
      const relX = ev.clientX - parentRect.left;
      const col = Math.max(0, Math.min(areaNames.length - 1, Math.floor(relX / colWidth)));

      setLocalRange((prev) => {
        if (edge === 'left') {
          const newStart = Math.min(col, prev.endCol);
          return { startCol: newStart, endCol: prev.endCol };
        } else {
          const newEnd = Math.max(col, prev.startCol);
          return { startCol: prev.startCol, endCol: newEnd };
        }
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setTimeout(() => { resizingRef.current = false; }, 0);
      setLocalRange((final) => {
        commitCoverage(final);
        return final;
      });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [areaNames.length, locked, commitCoverage]);

  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSpanClick = useCallback(() => {
    if (resizingRef.current || locked) return;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      setRenaming(true);
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        onCyclePriority?.(entry);
      }, 250);
    }
  }, [locked, entry, onCyclePriority]);

  if (!editMode) {
    const cls =
      'ivt-sm-span' +
      (highlighted ? ' ivt-sm-span--highlighted' : '') +
      (dimmed ? ' ivt-sm-span--dimmed' : '');
    return (
      <div
        className={cls}
        style={{
          ...style,
          background: PRIORITY_BG[tier],
          borderColor: PRIORITY_BORDER[tier],
        }}
      >
        <span className="ivt-sm-span__name">{useCase}</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="ivt-sm-span ivt-sm-span--editing"
      style={{
        ...style,
        background: PRIORITY_BG_EDIT[tier],
      }}
    >
      <div
        className="ivt-sm-span__edge ivt-sm-span__edge--left"
        onMouseDown={(e) => handleEdgeMouseDown('left', e)}
        title="Drag to adjust left boundary"
      />

      {renaming ? (
        <textarea
          ref={textareaRef}
          className="ivt-sm-span__rename-input"
          defaultValue={useCase}
          onBlur={commitRename}
          onKeyDown={handleKeyDown}
          disabled={locked}
        />
      ) : (
        <span
          className="ivt-sm-span__name ivt-sm-span__name--editable"
          onClick={handleSpanClick}
          title="Click to cycle priority; double-click to rename"
        >
          {useCase}
        </span>
      )}

      <div className="ivt-sm-cell__right-col">
        <button
          type="button"
          className="ivt-sm-cell__del-btn"
          disabled={locked}
          onClick={(e) => { e.stopPropagation(); onSoftDelete?.(entry); }}
          title="Delete"
        >
          ✕
        </button>
        <span className="ivt-sm-cell__drag-handle" title="Drag to reorder">
          <svg width="10" height="26" viewBox="0 0 10 26" fill="none">
            <circle cx="5" cy="4" r="3" fill="#d97706" stroke="#92400e" strokeWidth="1"/>
            <circle cx="5" cy="13" r="3" fill="#2563eb" stroke="#1d4ed8" strokeWidth="1"/>
            <circle cx="5" cy="22" r="3" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
          </svg>
        </span>
      </div>

      <div
        className="ivt-sm-span__edge ivt-sm-span__edge--right"
        onMouseDown={(e) => handleEdgeMouseDown('right', e)}
        title="Drag to adjust right boundary"
      />
    </div>
  );
}
